/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { ButtonProps } from '../../types';
import Icon from '../Icon/Icon';
import './Button.less';

const Spinner = () => (
  <svg className="button__spinner" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
    <path d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'large',
      disabled = false,
      loading = false,
      iconLeft,
      iconRight,
      children,
      onClick,
      className,
      htmlType = 'button',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        {...rest}
        ref={ref}
        type={htmlType}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        onClick={isDisabled ? undefined : onClick}
        className={cx(
          'button',
          `button--${variant}`,
          `button--${size}`,
          {
            'button--has-icon-left': !!iconLeft,
            'button--has-icon-right': !!iconRight,
            'button--loading': loading,
          },
          className
        )}>
        {/* Original content — always rendered to preserve width, invisible when loading */}
        <span className={cx('button__content', { 'button__content--hidden': loading })}>
          {iconLeft && (
            <span className="button__icon">
              <Icon name={iconLeft} />
            </span>
          )}
          {children}
          {iconRight && (
            <span className="button__icon">
              <Icon name={iconRight} />
            </span>
          )}
        </span>

        {/* Spinner overlay — absolutely centered on top */}
        {loading && (
          <span className="button__spinner-overlay">
            <Spinner />
          </span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
